import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, MapPin, Phone, ChevronRight, Loader2, ShieldAlert } from "lucide-react";
import { api } from "../lib/api";
import SEO from "../components/SEO";
import ActiveCaseAlertsForm from "../components/ActiveCaseAlertsForm";

const STATUS_TONE = {
  "Help Identify":            "bg-red-600 text-white border-red-700",
  "Identified":               "bg-amber-500 text-zinc-900 border-amber-700",
  "Arrested — Case Pending":  "bg-amber-600 text-white border-amber-800",
  "Charged":                  "bg-amber-500 text-zinc-900 border-amber-700",
  "On Trial":                 "bg-amber-500 text-zinc-900 border-amber-700",
  "Convicted":                "bg-zinc-900 text-white border-zinc-900",
};

const STATUS_LABEL_SHORT = {
  "Help Identify":            "HELP IDENTIFY",
  "Identified":               "IDENTIFIED",
  "Arrested — Case Pending":  "ARRESTED — CASE PENDING",
  "Charged":                  "CHARGED",
  "On Trial":                 "ON TRIAL",
  "Convicted":                "CONVICTED",
};

const FILTER_CHIPS = ["", "Help Identify", "Identified", "Arrested — Case Pending", "Charged", "On Trial"];

export default function ActiveCases() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/active-cases", { params: status ? { status } : {} });
        if (!cancelled) setItems(data.items || []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [status]);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12" data-testid="active-cases-page">
      <SEO
        title="Active Cases — Help Identify · Paws on Record"
        description="Open animal-cruelty cases where law enforcement is publicly asking for help identifying suspects or vehicles. Tips go to the investigating agency, not Paws on Record."
        path="/active-cases"
      />

      <div className="label-eyebrow flex items-center gap-2 text-red-600">
        <ShieldAlert size={12} /> Section · Active cases
      </div>
      <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-2 leading-tight">
        Active Cases —{" "}
        <span className="relative inline-block">
          <span className="relative z-10">Help Identify.</span>
          <span className="absolute left-0 right-0 bottom-1 h-3 bg-red-600/90 -z-0" />
        </span>
      </h1>
      <p className="mt-6 text-lg text-zinc-700 max-w-3xl leading-relaxed">
        Open animal-cruelty cases where police or animal control have publicly asked the community for information.
        <strong> Recognize a person or vehicle? Call the investigating agency directly</strong> — tips go to law enforcement, not Paws on Record.
      </p>

      {/* Hard rules banner */}
      <div className="mt-6 border border-zinc-900 bg-amber-50 p-4 text-sm text-zinc-900 flex items-start gap-3" data-testid="active-cases-disclaimer">
        <AlertTriangle size={18} className="shrink-0 mt-0.5 text-red-600" />
        <div className="leading-relaxed">
          <strong>Do not confront or publicly identify individuals based on suspicion.</strong>{" "}
          If you have information, contact the investigating law-enforcement agency directly. Paws on Record does not accept
          or publish accusations through user comments or submissions.
        </div>
      </div>

      {/* Status filter */}
      <div className="mt-8 flex items-center gap-2 flex-wrap">
        <span className="label-eyebrow mr-1">Filter:</span>
        {FILTER_CHIPS.map((s) => (
          <button
            key={s || "all"}
            type="button"
            onClick={() => setStatus(s)}
            data-testid={`active-cases-filter-${(s || "all").toLowerCase().replace(/\s+/g, "-")}`}
            className={`px-3 py-1.5 text-[11px] uppercase tracking-widest font-bold border ${
              status === s ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-800 border-zinc-300 hover:bg-zinc-100"
            }`}
          >
            {s || "All open"}
          </button>
        ))}
      </div>

      {/* List */}
      <div className="mt-6">
        {loading ? (
          <div className="p-10 text-center text-sm text-zinc-500 inline-flex items-center gap-2">
            <Loader2 size={14} className="animate-spin" /> Loading…
          </div>
        ) : items.length === 0 ? (
          <div className="border border-dashed border-zinc-400 p-10 text-center" data-testid="active-cases-empty">
            <ShieldAlert size={32} className="mx-auto text-zinc-400" />
            <div className="font-display font-black text-xl mt-3">No active cases in this view.</div>
            <p className="text-sm text-zinc-500 mt-2">Convicted cases live on the main registry.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="active-cases-grid">
            {items.map((c) => (<ActiveCaseCard key={c.id} c={c} />))}
          </div>
        )}
      </div>

      {/* Alert subscriptions */}
      <div className="mt-14 max-w-3xl">
        <ActiveCaseAlertsForm />
      </div>
    </div>
  );
}

function ActiveCaseCard({ c }) {
  const tone = STATUS_TONE[c.status] || STATUS_TONE["Help Identify"];
  const label = STATUS_LABEL_SHORT[c.status] || c.status;
  return (
    <Link
      to={`/active-cases/${c.id}`}
      className="group border border-zinc-900 bg-white hover-shadow-harsh flex flex-col"
      data-testid={`active-case-card-${c.id}`}
    >
      {/* Status strip */}
      <div className={`px-4 py-2 border-b border-zinc-900 flex items-center gap-2 text-[11px] uppercase tracking-widest font-bold ${tone}`}>
        <AlertTriangle size={12} /> {label}
      </div>
      {/* Photo */}
      <div className="relative aspect-[16/10] bg-zinc-100 overflow-hidden border-b border-zinc-200">
        {c.photo_url ? (
          <img
            src={c.photo_url}
            alt={c.title}
            className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
            loading="lazy"
            referrerPolicy="no-referrer"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-zinc-400">
            <ShieldAlert size={32} />
          </div>
        )}
      </div>
      {/* Body */}
      <div className="p-4 flex-1 flex flex-col">
        <div className="label-eyebrow text-zinc-500 flex items-center gap-1">
          <MapPin size={10} /> {c.city}, {c.state_code || c.state}
        </div>
        <h3 className="font-display font-black text-base leading-tight mt-1" data-testid={`active-case-title-${c.id}`}>
          {c.title}
        </h3>
        <p className="text-xs text-zinc-700 mt-2 line-clamp-3 leading-relaxed">
          {c.summary}
        </p>
        <div className="mt-3 pt-3 border-t border-zinc-200 flex items-center justify-between text-[11px] font-mono text-zinc-600">
          <span className="inline-flex items-center gap-1"><Phone size={10}/> {c.agency_phone || c.agency_name}</span>
          <span className="inline-flex items-center gap-1 group-hover:text-red-600">View case <ChevronRight size={12}/></span>
        </div>
      </div>
    </Link>
  );
}
